import axios from "axios";
import { z } from "zod";
import { environment } from "../environment";
import { errorLog } from "../logger";
import { AdvisorId } from "./Types";
import ValidationError from "./ZodError";
function transformHousehold(i) {
    switch (i) {
        case 0:
            return "Single income, no children";
        case 1:
            return "Single income, at least one child";
        case 2:
            return "Dual income, no children";
        case 3:
            return "Dual income, at least one child";
        case 4:
            return "Retired or financially independent";
        default:
            return "N/A"; //We have added to handle i:any type
    }
}
function transformIncomeRangeId(i) {
    switch (i) {
        case 0:
        case 1:
            return "$0 - 150k";
        case 2:
            return "$150k - 250k";
        case 3:
            return "$250k - 500k";
        case 4:
            return "$500k - 1MM";
        case 5:
            return "$1MM+";
        default:
            return "$0 - 150k";
    }
}
export const defaultMetaData = {
    Isadvisor: true,
    Advisorid: null,
};
const Advisor = z.object({
    first_name: z.string(),
    id: z.number(),
    last_name: z.string(),
});
const HouseholdEnum = z.custom((val) => typeof val === "number");
const IncomeRangeEnum = z.custom((val) => typeof val === "number");
const AssetEnum = z.custom((val) => typeof val === "number");
const Approval = z.enum(["pending", "approved", "rejected"]);
const ApexUser = z.object({
    assets: z.number().nullable(),
    client_approval: Approval.nullable(),
    cockroach_advisor_id: z.string(),
    custodian: z.literal("apex"),
    first_name: z.string().nullable(),
    household: HouseholdEnum.nullable(),
    id_user: z.number(),
    income: z.number().nullable(),
    income_range_id: IncomeRangeEnum.nullable(),
    last_name: z.string().nullable(),
    person_id: z.null(),
    phone: z.string().nullable(),
    risk_profile_id: z.number().nullable(),
    email: z.string(),
});
const MultiUser = z.object({
    assets: z.number().nullable(),
    client_approval: Approval.nullable(),
    cockroach_advisor_id: AdvisorId,
    custodian: z.literal("multi"),
    first_name: z.string(),
    household: HouseholdEnum.nullable(),
    id_user: z.null(),
    income: z.number().nullable(),
    income_range_id: IncomeRangeEnum.nullable(),
    last_name: z.string(),
    person_id: z.string(),
    phone: z.string().nullable(),
    risk_profile_id: z.number().nullable(),
});
const User = z.union([MultiUser, ApexUser]);
function isMultiCustodianUser(u) {
    return u.custodian === "multi";
}
const ResponseBody = z.object({
    cockroach_advisor_id: AdvisorId,
    error: z.enum(["False", "True"]),
    message: z.string(),
    status: z.enum(["True", "False"]),
    status_code: z.number(),
    advisors: Advisor.array(),
    users: User.array(),
});
export const url = () => environment() === "PROD"
    ? "https://middleware.prod.farther.com/flask/fartherapex/v1/advisors/advisorLogin"
    : "https://middleware.uat.farther.com/flask/fartherapex/v1/advisors/advisorLogin";
export default async function multiCustodianAdvisorLogin(magicToken, advisorEmail, metadata) {
    const res = await axios.post(url(), {
        metadata: metadata,
        payload: {
            email: advisorEmail,
        },
    }, {
        headers: {
            Authentication: magicToken,
            "Content-Type": "application/json;charset=UTF-8",
        },
    });
    const decoded = ResponseBody.safeParse(res.data);
    if (decoded.success) {
        const users = decoded.data.users
            .filter(isMultiCustodianUser)
            .map(({ income: _, income_range_id, ...u }) => ({
            ...u,
            household: u.household ? transformHousehold(u.household) : null,
            income: income_range_id
                ? transformIncomeRangeId(income_range_id)
                : null,
        }));
        const advisorId = decoded.data.cockroach_advisor_id;
        const advisors = decoded.data.advisors;
        return { jwt: magicToken, advisorId, users, advisors };
    }
    else {
        const errorMessage = "Could not decode advisor login response";
        errorLog("AdvisorLogin", `${decoded.error.message} ${JSON.stringify(decoded.error.issues)}`);
        throw new ValidationError(errorMessage, decoded.error.issues);
    }
}
//# sourceMappingURL=advisorLogin.js.map